import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FaSearch, FaPlus, FaTrash, FaEdit, FaCheckDouble, FaEllipsisV, FaUser, FaEnvelope, FaComment } from 'react-icons/fa';
import { BsPatchQuestionFill } from 'react-icons/bs';
import './Questions.css';
import axiosInstance from '../../utils/axiosInstance';

const REFRESH_MS = 30000;

// Last loaded questions of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const QUESTIONS_CACHE_PREFIX = 'questions_cache_';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const toast = (icon, title) =>
  Swal.fire({ icon, title, toast: true, position: 'top-end', timer: 2200, showConfirmButton: false });

const errorText = (err, fallback) =>
  err?.response?.data?.error || err?.response?.data?.detail || fallback;

const Questions = () => {
  const { shopId } = useParams();
  const cacheKey = `${QUESTIONS_CACHE_PREFIX}${shopId || 'all'}`;

  // The shop is known from the very first render, and the saved list of that shop
  // is shown instantly while fresh data loads in the background.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const cached = readJson(cacheKey);
    initialRef.current = Array.isArray(cached) ? cached : null;
  }

  const [questions, setQuestions] = useState(initialRef.current || []);
  // True only while there is nothing to show yet (so "No questions found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [userName, setUserName] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  // chatbot memory: the answer the website chatbot gives for this question
  const [answer, setAnswer] = useState('');
  const [keywords, setKeywords] = useState('');
  const [useInBot, setUseInBot] = useState(true);
  const [onlyUnanswered, setOnlyUnanswered] = useState(false);

  const requestRef = useRef(0);
  const prevKeyRef = useRef(shopId || 'all');

  const params = useMemo(() => (shopId ? { shop_id: shopId } : {}), [shopId]);

  // local changes (add / edit / delete) are saved too, so the saved copy is always correct
  const updateQuestions = (updater) => {
    setQuestions((prev) => {
      const next = updater(prev);
      writeJson(cacheKey, next);
      return next;
    });
  };

  const load = useCallback(
    async (silent = false) => {
      // Only the latest request is allowed to update the screen
      const requestId = ++requestRef.current;
      try {
        const res = await axiosInstance.get('/feedback/questions/', { params });
        if (requestId !== requestRef.current) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setQuestions(list);
        writeJson(cacheKey, list);
        setError('');
      } catch (err) {
        if (requestId !== requestRef.current) return;
        if (!silent) setError(errorText(err, 'Could not load questions.'));
      } finally {
        if (requestId === requestRef.current) setIsLoading(false);
      }
    },
    [params, cacheKey]
  );

  useEffect(() => {
    const key = shopId || 'all';
    // Shop changed: show the saved list of this shop immediately (if there is one)
    if (prevKeyRef.current !== key) {
      prevKeyRef.current = key;
      const cached = readJson(cacheKey);
      if (Array.isArray(cached)) {
        setQuestions(cached);
        setIsLoading(false);
      } else {
        setQuestions([]);
        setIsLoading(true);
      }
      setSelectedQuestions([]);
    }

    load();
    const timer = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const unansweredCount = questions.filter((q) => !(q.answer || '').trim()).length;

  const filteredQuestions = questions.filter((q) => {
    if (onlyUnanswered && (q.answer || '').trim()) return false;
    const term = searchTerm.toLowerCase();
    return (
      (q.message || '').toLowerCase().includes(term) ||
      (q.userName || '').toLowerCase().includes(term) ||
      (q.email || '').toLowerCase().includes(term) ||
      (q.answer || '').toLowerCase().includes(term)
    );
  });

  const handleSelectAll = () => {
    if (selectedQuestions.length === filteredQuestions.length) setSelectedQuestions([]);
    else setSelectedQuestions(filteredQuestions.map((q) => q.id));
    setShowActionDropdown(false);
  };

  const handleSelectQuestion = (id) => {
    setSelectedQuestions((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setShowActionDropdown(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedQuestions.length === 0) return;
    const result = await Swal.fire({
      title: `Delete ${selectedQuestions.length} question(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
    });
    if (!result.isConfirmed) return;

    try {
      await Promise.all(selectedQuestions.map((id) => axiosInstance.delete(`/feedback/questions/${id}/`, { params })));
      updateQuestions((prev) => prev.filter((q) => !selectedQuestions.includes(q.id)));
      setSelectedQuestions([]);
      setShowActionDropdown(false);
      toast('success', 'Deleted');
    } catch (err) {
      toast('error', errorText(err, 'Could not delete.'));
      load(true);
    }
  };

  const resetForm = () => {
    setEditingQuestion(null);
    setUserName('');
    setMessage('');
    setEmail('');
    setAnswer('');
    setKeywords('');
    setUseInBot(true);
  };

  const handleOpenModalForNew = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleOpenModalForEdit = (q) => {
    setEditingQuestion(q);
    setUserName(q.userName || '');
    setMessage(q.message || '');
    setEmail(q.email || '');
    setAnswer(q.answer || '');
    setKeywords(q.keywords || '');
    setUseInBot(q.use_in_chatbot !== false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    resetForm();
  };

  const handleSave = async () => {
    // name / e-mail are optional now (questions that come from the website chat have no e-mail)
    if (!message.trim()) return toast('warning', 'Please enter the Question / Message');

    const body = {
      userName: userName.trim(),
      email: email.trim(),
      message: message.trim(),
      answer: answer.trim(),
      keywords: keywords.trim(),
      use_in_chatbot: useInBot,
    };

    setSaving(true);
    try {
      if (editingQuestion) {
        const res = await axiosInstance.put(`/feedback/questions/${editingQuestion.id}/`, body, { params });
        updateQuestions((prev) => prev.map((q) => (q.id === res.data.id ? res.data : q)));
        toast('success', 'Question updated');
      } else {
        const res = await axiosInstance.post('/feedback/questions/', body, { params });
        updateQuestions((prev) => [res.data, ...prev]);
        toast('success', 'Question added');
      }
      setIsModalOpen(false);
      resetForm();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Not saved', text: errorText(err, 'Something went wrong.'), confirmButtonColor: '#004aad' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingQuestion) return;
    const result = await Swal.fire({
      title: 'Delete this question?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
    });
    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`/feedback/questions/${editingQuestion.id}/`, { params });
      updateQuestions((prev) => prev.filter((q) => q.id !== editingQuestion.id));
      setSelectedQuestions((prev) => prev.filter((id) => id !== editingQuestion.id));
      setIsModalOpen(false);
      resetForm();
      toast('success', 'Question deleted');
    } catch (err) {
      toast('error', errorText(err, 'Could not delete.'));
    }
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (showActionDropdown && !e.target.closest('.action-dropdown-container')) setShowActionDropdown(false);
    };
    document.addEventListener('click', onClickOutside);
    return () => document.removeEventListener('click', onClickOutside);
  }, [showActionDropdown]);

  return (
    <div className="questions-main-container">
      <div className="questions-top-bar">
        <div className="header-content">
          <BsPatchQuestionFill
            className="questions-title-icon"
            style={{ width: '1.6rem', height: '1.6rem', minWidth: '1.6rem', color: '#004aad', fill: '#004aad', display: 'inline-block', flexShrink: 0 }}
          />
          <h2>Questions Management</h2>
        </div>

        <div className="questions-action-section">
          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, email or question..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="action-buttons">
            <button
              className="select-all-btn"
              onClick={() => setOnlyUnanswered((v) => !v)}
              title="Questions the chatbot could not answer"
              style={onlyUnanswered ? { background: '#004aad', color: '#fff' } : undefined}
            >
              <FaComment />
              <span>Unanswered ({unansweredCount})</span>
            </button>
            <button className="select-all-btn" onClick={handleSelectAll}>
              <FaCheckDouble />
              <span>{selectedQuestions.length === filteredQuestions.length && filteredQuestions.length > 0 ? 'Deselect All' : 'Select All'}</span>
            </button>
            <button className="add-question-btn" onClick={handleOpenModalForNew}>
              <FaPlus />
              <span>ADD QUESTION</span>
            </button>
          </div>
        </div>
      </div>

      <div className="questions-content">
        {error && <div className="comm-error">{error}</div>}

        {selectedQuestions.length > 0 && (
          <div className="selected-summary">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">{selectedQuestions.length} question(s) selected</span>
              <div className="action-dropdown-container">
                <button className="action-dropdown-btn" onClick={() => setShowActionDropdown(!showActionDropdown)}>
                  <FaEllipsisV />
                  <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="action-dropdown-menu">
                    <button className="dropdown-item delete-item" onClick={handleDeleteSelected}>
                      <FaTrash />
                      <span>Delete Selected ({selectedQuestions.length})</span>
                    </button>
                  </div>
                )}
              </div>
              <button className="clear-selection" onClick={() => { setSelectedQuestions([]); setShowActionDropdown(false); }}>
                Clear all
              </button>
            </div>
          </div>
        )}

        <div className="questions-table-container">
          <div className="questions-table-wrapper">
            <table className="questions-table">
              <thead>
                <tr>
                  <th className="col-check">
                    <input
                      type="checkbox"
                      checked={selectedQuestions.length === filteredQuestions.length && filteredQuestions.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Question</th>
                  <th>Answer (chatbot)</th>
                  <th>From</th>
                  <th>Date</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Nothing is shown until the first load is done, so "No questions found" never flashes */}
                {isLoading ? (
                  <tr>
                    <td colSpan="6" style={{ height: '200px' }}></td>
                  </tr>
                ) : (
                  filteredQuestions.map((q) => (
                    <tr key={q.id} className={selectedQuestions.includes(q.id) ? 'selected' : ''}>
                      <td className="checkbox-cell">
                        <input type="checkbox" checked={selectedQuestions.includes(q.id)} onChange={() => handleSelectQuestion(q.id)} />
                      </td>
                      <td className="question-cell">
                        <FaComment className="question-icon" />
                        <span className="question-text">{q.message}</span>
                      </td>
                      <td style={{ maxWidth: 320 }}>
                        {q.answer ? (
                          <>
                            <span className="question-text">{q.answer}</span>
                            {q.use_in_chatbot === false && (
                              <div style={{ fontSize: 12, color: '#b45f06' }}>Not used by the chatbot</div>
                            )}
                          </>
                        ) : (
                          <span style={{ color: '#d9534f', fontWeight: 600 }}>No answer yet - click edit to answer</span>
                        )}
                      </td>
                      <td>
                        <div>{q.userName}</div>
                        <div style={{ fontSize: 12, color: '#7a869a' }}>{q.email}</div>
                      </td>
                      <td>{q.messageDate}</td>
                      <td className="actions-cell">
                        <button className="edit-question-btn" onClick={() => handleOpenModalForEdit(q)} title="Edit Question">
                          <FaEdit />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!isLoading && filteredQuestions.length === 0 && (
            <div className="no-questions">
              <BsPatchQuestionFill className="no-data-icon" />
              <p>No questions found.</p>
              <button className="add-first-btn" onClick={handleOpenModalForNew}>
                <FaPlus /> Add Your First Question
              </button>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingQuestion ? 'Edit Question' : 'Add New Question'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              <form className="question-form" onSubmit={(e) => e.preventDefault()}>
                <div className="form-row">
                  <label className="form-label"><FaUser className="form-icon" /> User Name:</label>
                  <input type="text" value={userName} onChange={(e) => setUserName(e.target.value)} placeholder="Enter user name" className="question-input" />
                </div>
                <div className="form-row">
                  <label className="form-label"><FaEnvelope className="form-icon" /> Email:</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter email address" className="question-input" />
                </div>
                <div className="form-row">
                  <label className="form-label"><FaComment className="form-icon" /> Question:</label>
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Enter the question" rows="4" maxLength={520} className="question-textarea" />
                </div>
                <div className="form-row">
                  <label className="form-label"><FaComment className="form-icon" /> Answer (chatbot reply):</label>
                  <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="The chatbot on the website will learn this answer and use it for the same question" rows="4" maxLength={1500} className="question-textarea" />
                </div>
                <div className="form-row">
                  <label className="form-label">Extra keywords (optional, comma separated):</label>
                  <input type="text" value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="delivery, shipping, courier" className="question-input" maxLength={300} />
                </div>
                <div className="form-row">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={useInBot} onChange={(e) => setUseInBot(e.target.checked)} />
                    Let the website chatbot use this question and answer
                  </label>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCloseModal} disabled={saving}>Cancel</button>
              {editingQuestion && (
                <button className="btn-delete" onClick={handleDelete} disabled={saving}>Delete</button>
              )}
              <button className="btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editingQuestion ? 'Update Question' : 'Save Question'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Questions;